import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldValidator, useFormikContext } from 'formik';
import { forwardRef, InputHTMLAttributes, ReactNode, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import type { RectReadOnly } from 'react-use-measure';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import { FormFieldProps } from 'src/components/form/form-field-props';
import { InnerAddon } from 'src/components/form/inner-addon';
import { Label } from 'src/components/form/label';
import {
  borderRadius,
  borderWidth,
  placeholderStyle,
} from 'src/components/form/styles';
import { useCustomField } from 'src/components/form/use-custom-field';
import { OnShowError } from 'src/components/form/use-error-handler';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Stack } from 'src/components/layout/stack';
import { Tooltip } from 'src/components/overlay/tooltip';
import { Hint } from 'src/components/text/hint';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useId } from 'src/hooks/use-id';
import { useWithinStoryPage } from 'src/hooks/use-within-story-page';
import { Colors } from 'src/styles';
import styled, { css } from 'styled-components';

export const SimpleInput = styled.input<{
  error?: boolean;
  leftBounds?: RectReadOnly;
  rightBounds?: RectReadOnly;
}>`
  // inline-block instead block needed to display correct baseline for Firefox
  display: inline-block;
  vertical-align: baseline;
  width: 100%;
  font-style: none;
  border: ${borderWidth} solid ${Colors.brand};
  color: ${Colors.brand};
  border-radius: ${borderRadius};
  padding-top: 0.6rem;
  padding-bottom: 0.7rem;
  padding-left: ${({ leftBounds }) =>
    leftBounds ? leftBounds.width + 'px' : '2rem'};
  padding-right: ${({ rightBounds }) =>
    rightBounds ? rightBounds.width + 'px' : '2rem'};
  line-height: normal; /* needed on iOS for centering the placeholder */

  ${({ placeholder }) =>
    placeholder &&
    css`
      ::placeholder {
        ${placeholderStyle};
      }
    `}

  ${({ error }) =>
    error &&
    css`
      border-color: ${Colors.error};
      background-color: ${Colors.errorLight};
      color: ${Colors.error};

      ::placeholder {
        color: ${Colors.error};
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      border-color: ${Colors.inactive};
      background: ${Colors.inactiveLight};
      cursor: not-allowed;
      color: ${Colors.inactive};

      ::placeholder {
        color: ${Colors.inactive};
      }
    `}
`;

export type InputProps = {
  stacked?: boolean;
  nested?: boolean;
  validate?: FieldValidator;
  onShowError?: OnShowError;
  hideLabel?: boolean;
  /**
   * @deprecated Use this pattern to create custom error messages.
   *
   * ```
   * import { number } from 'yup';
   *
   * const validation = number
   *   .test(
   *     'No17',
   *     () => (label: string) => `${label} can't be 17.`,
   *     (value, context) => value !== 17
   *   );
   * ```
   */
  getCustomErrorMessage?: (message: string) => string | undefined;
  addon?: ReactNode;
  unit?: string;
} & FormFieldProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'value'>;

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { minTablet } = useBreakpoints();
  const {
    label,
    name,
    stacked = !minTablet,
    nested,
    validate,
    hint,
    onShowError,
    hideLabel = false,
    getCustomErrorMessage,
    addon,
    unit,
    info,
    markAsRequired,
    ...inputProps
  } = props;
  const id = useId();
  const withinStoryPage = useWithinStoryPage();

  const [field, showError, error] = useCustomField<string | null>(name, label, {
    validate,
    onShowError,
  });

  const { submitCount, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (inputProps.type === 'password') return;
    if (typeof field.value !== 'string') return;
    const trimmed = field.value.trim();
    if (trimmed === field.value) return;
    setFieldValue(name, trimmed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  const formGroup = useFieldGroup();
  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  const focusRef = useFocusManager();

  const fieldId = formGroup?.id || id;
  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;
  const ariaLabel = hideLabel ? displayLabel : undefined;

  const fieldItem = (
    <FieldItem>
      <InnerAddon
        showError={showError}
        disabled={inputProps.disabled}
        right={
          unit ? (
            <small
              data-testid={`${field.name}Unit`}
              style={{ paddingTop: '0.5rem' }}
            >
              {unit}
            </small>
          ) : undefined
        }
      >
        {({ rightBounds }) => (
          <SimpleInput
            ref={mergeRefs([focusRef, ref])}
            id={fieldId}
            error={showError}
            data-test-error={showError}
            data-testid={field.name}
            {...field}
            aria-label={ariaLabel}
            onWheel={(e) => e.currentTarget.blur()}
            {...inputProps}
            // if we have multiple pages embedded in a docs view,
            // we don't want autoFocus to interfere with Storybooks
            // scroll position handling
            // note: useWithinStoryPage currently doesn't differentiate
            // between docs view and canvas view. for now autoFocus
            // will also be deactivated in the canvas view.
            autoFocus={withinStoryPage ? false : props.autoFocus}
            // if value is null, default to empty string
            value={field.value ?? ''}
            rightBounds={rightBounds}
          />
        )}
      </InnerAddon>
      {hint && (
        <Hint disabled={props.disabled} mode="formInput">
          {hint}
        </Hint>
      )}
      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
          getCustomErrorMessage={getCustomErrorMessage}
        />
      )}
    </FieldItem>
  );

  const wrappedFieldItem = addon ? (
    <Stack
      flow="column"
      gap={1}
      templateColumns="1fr auto"
      alignItems="baseline"
    >
      {fieldItem} {addon}
    </Stack>
  ) : (
    fieldItem
  );

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked}>
      <Label
        htmlFor={fieldId}
        error={showError}
        nested={nested}
        data-testid={`${field.name}Label`}
      >
        {displayLabel}

        {info && (
          <>
            &nbsp;
            <Tooltip content={info}>
              {(targetProps) => (
                <FontAwesomeIcon {...targetProps} icon={faInfoCircle} />
              )}
            </Tooltip>
          </>
        )}
      </Label>

      {wrappedFieldItem}
    </FieldLayout>
  ) : (
    wrappedFieldItem
  );
});
